import React, { useCallback, useEffect, useState } from "react";
import { MessageSquareText, RefreshCw, Plus, Upload, Search, X, Filter, ChevronDown, Link2, FileSpreadsheet, Sparkles, UserCheck } from "lucide-react";
import StarFilter from "./reviews/StarFilter";
import { toast } from "sonner";
import { reviewsApi, SOURCE_META, inputCls, btnCls } from "./reviews/shared";
import ReviewRow from "./reviews/ReviewRow";
import MatchModal from "./reviews/MatchModal";
import AddReviewModal from "./reviews/AddReviewModal";
import AssignModal from "./reviews/AssignModal";
import ImportModal from "./reviews/ImportModal";
import StatsStrip from "./reviews/StatsStrip";
import MonthlySummary from "./reviews/MonthlySummary";
import "./reviews/light.css";
import GarsonKarnesi from "./reviews/GarsonKarnesi";
import SourcesTab from "./reviews/SourcesTab";
import DisputesTab from "./reviews/DisputesTab";
import DisputePanel from "./reviews/DisputePanel";
import { Modal } from "./reviews/shared";

const PAGE = 50;
const EMPTY_F = { source: "", ratings: "", garson: "", boat_id: "", segment: "", matched: "", q: "", date_from: "", date_to: "", date_by: "", place: "", dispute: "" };

export default function Reviews() {
  const [tab, setTab] = useState("list");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [capped, setCapped] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [sources, setSources] = useState([]);
  const [garsonlar, setGarsonlar] = useState([]);
  const [boats, setBoats] = useState([]);
  const [places, setPlaces] = useState([]);
  const [assignFor, setAssignFor] = useState(null);
  const [f, setF] = useState(EMPTY_F);
  const [q, setQ] = useState("");
  const [matchFor, setMatchFor] = useState(null);
  const [page, setPage] = useState(0);
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [disputeFor, setDisputeFor] = useState(null);

  const setFilter = (k, v) => setF(p => ({ ...p, [k]: v }));

  const load = useCallback(async (p = 0) => {
    setLoading(true);
    try {
      const params = { offset: p * PAGE, limit: PAGE };
      Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
      const d = await reviewsApi.list(params);
      setItems(d.reviews);
      setTotal(d.total); setCapped(!!d.total_capped); setHasMore(d.has_more);
      setPage(p);
    } catch (e) { toast.error("Yorumlar yüklenemedi"); }
    setLoading(false);
  }, [f]);

  const loadMeta = useCallback(async () => {
    try { setStats(await reviewsApi.stats()); } catch {}
    try { setSources((await reviewsApi.sources()).sources || []); } catch {}
  }, []);

  useEffect(() => { load(0); }, [load]);
  useEffect(() => {
    loadMeta();
    reviewsApi.garsonlar().then(d => setGarsonlar((d?.garsonlar || []).filter(g => g.active !== false)));
    reviewsApi.boats().then(d => setBoats((d?.boats || []).filter(b => b.active !== false)));
    reviewsApi.places().then(d => setPlaces(d?.places || [])).catch(() => {});
  }, [loadMeta]);
  useEffect(() => { const t = setTimeout(() => setFilter("q", q.trim()), 350); return () => clearTimeout(t); }, [q]);

  const syncRunning = sources.some(s => s.running || s.last_status === "running");
  useEffect(() => {
    if (!syncRunning) return;
    const t = setInterval(async () => {
      try {
        const d = await reviewsApi.sources(); setSources(d.sources || []);
        if (!(d.sources || []).some(s => s.running || s.last_status === "running")) { load(0); reviewsApi.stats().then(setStats).catch(() => {}); }
      } catch {}
    }, 3000);
    return () => clearInterval(t);
  }, [syncRunning, load]);

  const refreshAll = () => { load(0); loadMeta(); };

  const unmatch = async (r) => {
    try { await reviewsApi.unmatch(r.id); toast.success("Eşleşme kaldırıldı"); refreshAll(); }
    catch { toast.error("İşlem başarısız"); }
  };
  const remove = async (r) => {
    if (!window.confirm("Bu yorum silinsin mi?")) return;
    try { await reviewsApi.remove(r.id); toast.success("Yorum silindi"); refreshAll(); }
    catch { toast.error("Silinemedi"); }
  };
  const rematch = async () => {
    try { const d = await reviewsApi.rematch(); toast.success(`${d.matched} / ${d.candidates} yorum eşleştirildi`); refreshAll(); }
    catch { toast.error("Eşleştirme çalıştırılamadı"); }
  };
  const [suggesting, setSuggesting] = useState(false);
  const suggest = async () => {
    setSuggesting(true);
    try { const d = await reviewsApi.suggest(120); toast.success(`${d.suggested} öneri üretildi (${d.candidates} aday)`); refreshAll(); if (d.suggested) setFilter("matched", "suggested"); }
    catch { toast.error("Öneri üretilemedi"); }
    setSuggesting(false);
  };
  const acceptSuggestion = async (r) => {
    try { await reviewsApi.match(r.id, { booking_id: r.suggested_booking_id }); toast.success(`${r.suggested_code || "Rezervasyon"} ile eşleştirildi`); refreshAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Eşleştirme başarısız"); }
  };
  const rejectSuggestion = async (r) => {
    try { await reviewsApi.rejectSuggestion(r.id); toast.success("Öneri reddedildi"); refreshAll(); }
    catch { toast.error("İşlem başarısız"); }
  };

  const [acceptAll, setAcceptAll] = useState(null);
  const pollAcceptAll = useCallback(() => {
    const t = setInterval(async () => {
      try {
        const s = await reviewsApi.acceptAllStatus();
        setAcceptAll(s.running ? s : null);
        if (!s.running) {
          clearInterval(t);
          if (s.error) toast.error("Onaylama hatası: " + s.error);
          else if (s.total) toast.success(`${s.accepted} öneri onaylandı`);
          refreshAll();
        }
      } catch {}
    }, 1500);
    return t;
  }, []);
  const runAcceptAll = async () => {
    const n = stats?.totals?.suggested || 0;
    if (!window.confirm(`Bekleyen ${n} öneri, önerilen rezervasyonlarla eşleştirilecek ve yıldızlar Canlı Sayfa'ya işlenecek. Devam edilsin mi?`)) return;
    try {
      const d = await reviewsApi.acceptAllSuggestions();
      setAcceptAll({ running: true, total: d.total || n, done: 0, accepted: 0 });
      toast.message("Onaylama başladı — arka planda, parça parça işleniyor");
      pollAcceptAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Başlatılamadı"); }
  };
  useEffect(() => {
    reviewsApi.acceptAllStatus().then(s => { if (s.running) { setAcceptAll(s); pollAcceptAll(); } }).catch(() => {});
  }, [pollAcceptAll]);

  const creditGarson = async (r, garson) => {
    try { await reviewsApi.creditGarson(r.id, garson); toast.success(`Kredi ${garson} garsonuna verildi`); refreshAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "İşlem başarısız"); }
  };
  const uncreditGarson = async (r) => {
    try { await reviewsApi.uncreditGarson(r.id); toast.success("Kredi geri alındı"); refreshAll(); }
    catch { toast.error("İşlem başarısız"); }
  };
  const unassignGarson = async (r) => {
    try { await reviewsApi.unassignGarson(r.id); toast.success("Garson ataması kaldırıldı"); refreshAll(); }
    catch { toast.error("İşlem başarısız"); }
  };
  const acceptCredit = async (r, garson) => {
    try { await reviewsApi.assignGarson(r.id, garson); toast.success(`Kredi ${garson} garsonuna verildi`); refreshAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "İşlem başarısız"); }
  };
  const rejectCredit = async (r) => {
    try { await reviewsApi.rejectCreditSuggestion(r.id); toast.success("Garson önerisi reddedildi"); refreshAll(); }
    catch { toast.error("İşlem başarısız"); }
  };
  const [acceptCreditAll, setAcceptCreditAll] = useState(null);
  const pollAcceptCreditAll = useCallback(() => {
    const t = setInterval(async () => {
      try {
        const s = await reviewsApi.acceptAllCreditStatus();
        setAcceptCreditAll(s.running ? s : null);
        if (!s.running) {
          clearInterval(t);
          if (s.error) toast.error("Onaylama hatası: " + s.error);
          else if (s.total) toast.success(`${s.accepted} garson önerisi onaylandı`);
          refreshAll();
        }
      } catch {}
    }, 1500);
    return t;
  }, []);
  const runAcceptCreditAll = async () => {
    const n = stats?.totals?.garson_suggested || 0;
    if (!window.confirm(`Bekleyen ${n} garson önerisi, önerilen birincil garsona kredilenecek. Devam edilsin mi?`)) return;
    try {
      const d = await reviewsApi.acceptAllCredit();
      setAcceptCreditAll({ running: true, total: d.total || n, done: 0, accepted: 0 });
      toast.message("Onaylama başladı — arka planda işleniyor");
      pollAcceptCreditAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Başlatılamadı"); }
  };
  useEffect(() => {
    reviewsApi.acceptAllCreditStatus().then(s => { if (s.running) { setAcceptCreditAll(s); pollAcceptCreditAll(); } }).catch(() => {});
  }, [pollAcceptCreditAll]);
  const [mismatchScan, setMismatchScan] = useState(null);
  const runMismatchScan = async () => {
    try {
      const d = await reviewsApi.scanMismatch();
      setMismatchScan({ running: true, total: d.total || 0, done: 0, flagged: 0 });
      toast.message("Tarama başladı — arka planda çalışıyor");
      const t = setInterval(async () => {
        try {
          const s = await reviewsApi.scanMismatchStatus();
          setMismatchScan(s.running ? s : null);
          if (!s.running) { clearInterval(t); toast.success(`${s.flagged} garson uyuşmazlığı bulundu`); refreshAll(); }
        } catch {}
      }, 2000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Başlatılamadı"); }
  };
  useEffect(() => {
    reviewsApi.scanMismatchStatus().then(s => { if (s.running) setMismatchScan(s); }).catch(() => {});
  }, []);

  const [autoAssign, setAutoAssign] = useState(null);
  const runAutoAssign = async () => {
    try {
      const d = await reviewsApi.autoAssignGarson();
      setAutoAssign({ running: true, total: d.total || 0, done: 0, assigned: 0 });
      toast.message("Otomatik atama başladı — arka planda çalışıyor");
      const t = setInterval(async () => {
        try {
          const s = await reviewsApi.autoAssignStatus();
          setAutoAssign(s.running ? s : null);
          if (!s.running) { clearInterval(t); toast.success(`${s.assigned} yorum garsona atandı · ${s.suggested || 0} öneriye düştü · ${s.skipped} atlandı`); refreshAll(); if (s.suggested) setFilter("matched", "garson_suggested"); }
        } catch {}
      }, 2000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Başlatılamadı"); }
  };
  useEffect(() => {
    reviewsApi.autoAssignStatus().then(s => { if (s.running) { setAutoAssign(s); runAutoAssign._poll && runAutoAssign._poll(); } }).catch(() => {});
  }, []);

  const hasFilters = Object.values(f).some(Boolean);
  const [exporting, setExporting] = useState(false);
  const exportList = async () => {
    setExporting(true);
    try {
      const params = {}; Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
      await reviewsApi.download("/api/reviews/export", params, "yorumlar.xlsx");
      toast.success("Excel indirildi (filtrelere göre, en fazla 20.000 satır)");
    } catch { toast.error("Dışa aktarma başarısız"); }
    setExporting(false);
  };
  const tabs = [["list", "Yorumlar"], ["monthly", "Aylık Özet"], ["garson", "Garson Karnesi"], ["disputes", "İtiraz Takibi"], ["sources", "Kaynaklar"]];

  return (
    <div className="reviews-light min-h-full" data-testid="reviews-page">
      <div className="p-6 max-w-[1500px] mx-auto">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-500/15 flex items-center justify-center"><MessageSquareText className="w-5 h-5 text-amber-400" /></div>
          <div>
            <h1 className="text-xl font-bold text-white">Yorumlar</h1>
            <p className="text-xs text-slate-400">GYG · Viator · TripAdvisor · Google — en yeni üstte, booking ref ile Canlı Sayfa'ya bağlı</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={rematch} className={`${btnCls} bg-slate-700 hover:bg-slate-600 text-slate-200`} title="Ref'i olan eşleşmemiş yorumları yeniden dene" data-testid="rematch-btn"><Link2 className="w-4 h-4" /> Yeniden Eşleştir</button>
          <button onClick={suggest} disabled={suggesting} className={`${btnCls} bg-violet-700 hover:bg-violet-600 text-white`} title="Ref'siz yorumlar için yorumcu adı + tarih ile rezervasyon önerisi üret" data-testid="suggest-btn"><Sparkles className={`w-4 h-4 ${suggesting ? "animate-pulse" : ""}`} /> İsim Önerisi</button>
          <button onClick={runAutoAssign} disabled={!!autoAssign?.running} className={`${btnCls} bg-amber-600 hover:bg-amber-500 text-white`} title="Rezervasyonla eşleşmeyen ama yorumda adı geçen yorumları otomatik garsona atar (tüm garsonlar)" data-testid="auto-assign-btn"><UserCheck className={`w-4 h-4 ${autoAssign?.running ? "animate-pulse" : ""}`} /> {autoAssign?.running ? `Atanıyor ${autoAssign.done}/${autoAssign.total}` : "Adı Geçeni Ata"}</button>
          <button onClick={() => setShowImport(true)} className={`${btnCls} bg-slate-700 hover:bg-slate-600 text-slate-200`} data-testid="import-btn"><Upload className="w-4 h-4" /> Dosyadan Aktar</button>
          <button onClick={() => setShowAdd(true)} className={`${btnCls} bg-blue-600 hover:bg-blue-500 text-white`} data-testid="add-review-btn"><Plus className="w-4 h-4" /> Yorum Ekle</button>
          <button onClick={refreshAll} className={`${btnCls} bg-slate-700 hover:bg-slate-600 text-slate-200`} data-testid="reviews-refresh"><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /></button>
        </div>
      </div>

      <StatsStrip stats={stats} onUnmatchedClick={() => { setTab("list"); setFilter("matched", "no"); }} onSuggestedClick={() => { setTab("list"); setFilter("matched", "suggested"); }} onGarsonSuggestedClick={() => { setTab("list"); setFilter("matched", "garson_suggested"); }} onSourceClick={(src) => { setTab("list"); setFilter("source", src); setFilter("place", ""); }} activeSource={f.source} />

      <div className="flex items-center gap-2 mb-4">
        {tabs.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${tab === k ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-300 hover:bg-slate-700"}`}>{label}</button>
        ))}
      </div>

      {tab === "garson" && <GarsonKarnesi onGarsonClick={(g, ctx) => { setTab("list"); setF(p => ({ ...p, garson: g, segment: ctx?.segment || "", date_from: ctx?.from || "", date_to: ctx?.to || "", date_by: ctx?.by === "review" ? "" : "tour", matched: "", source: "", place: "", ratings: "", boat_id: "" })); }} />}
      {tab === "monthly" && <MonthlySummary onPick={(month, source) => {
        const [y, mo] = month.split("-"); const last = new Date(+y, +mo, 0).getDate();
        setTab("list"); setF(p => ({ ...p, source, date_from: `${month}-01`, date_to: `${month}-${String(last).padStart(2, "0")}`, matched: "no", place: "" }));
      }} />}
      {tab === "sources" && <SourcesTab sources={sources} onChanged={loadMeta} />}
      {tab === "disputes" && <DisputesTab />}

      {tab === "list" && (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <div className="relative flex-1 min-w-[220px] max-w-md">
              <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input value={q} onChange={e => setQ(e.target.value)} placeholder="Ara: müşteri, ref, garson, yorum metni…" className={`${inputCls} w-full pl-9 pr-8 py-2`} data-testid="reviews-search" />
              {q && <button onClick={() => setQ("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>}
            </div>
            <Filter className="w-4 h-4 text-slate-400 ml-2" />
            <select value={f.source} onChange={e => setFilter("source", e.target.value)} className={inputCls} data-testid="filter-source">
              <option value="">Tüm kaynaklar</option>
              {Object.entries(SOURCE_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
            </select>
            {places.length > 0 && (
              <select value={f.place} onChange={e => setFilter("place", e.target.value)} className={inputCls} data-testid="filter-place" title="Hangi Google konumu / Viator-TripAdvisor ürünü">
                <option value="">Tüm konumlar / ürünler</option>
                {Object.entries(places.reduce((acc, p) => { (acc[p.source] = acc[p.source] || []).push(p); return acc; }, {})).map(([src, list]) => (
                  <optgroup key={src} label={(SOURCE_META[src]?.label || src).toUpperCase()}>
                    {list.map(p => <option key={src + p.place_name} value={p.place_name}>{p.place_name.length > 48 ? p.place_name.slice(0, 48) + "…" : p.place_name} ({p.count}{p.avg_rating != null ? ` · ★${p.avg_rating}` : ""})</option>)}
                  </optgroup>
                ))}
              </select>
            )}
            <StarFilter value={f.ratings} onChange={v => setFilter("ratings", v)} />
            <select value={f.garson} onChange={e => setFilter("garson", e.target.value)} className={inputCls} data-testid="filter-garson">
              <option value="">Tüm garsonlar</option>
              {garsonlar.map(g => <option key={g.id} value={g.name}>{g.name}</option>)}
              {f.garson && !garsonlar.some(g => g.name === f.garson) && <option value={f.garson}>{f.garson}</option>}
            </select>
            <select value={f.boat_id} onChange={e => setFilter("boat_id", e.target.value)} className={inputCls} data-testid="filter-boat">
              <option value="">Tüm ürünler / tekneler</option>
              {boats.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
            <select value={f.segment} onChange={e => setFilter("segment", e.target.value)} className={inputCls} data-testid="filter-segment">
              <option value="">Dinner + Sunset</option>
              <option value="dinner">Dinner</option>
              <option value="sunset">Sunset</option>
            </select>
            <select value={f.matched} onChange={e => setFilter("matched", e.target.value)} className={inputCls} data-testid="filter-matched">
              <option value="">Eşleşme: hepsi</option>
              <option value="yes">Eşleşenler</option>
              <option value="no">Eşleşmeyenler</option>
              <option value="suggested">Öneri bekleyenler</option>
              <option value="garson_suggested">Garson önerisi (onay bekleyen)</option>
              <option value="garson_mismatch">Garson uyuşmazlığı</option>
              <option value="garson_assigned">Garsona atandı (rezervasyonsuz)</option>
              <option value="text_garson">Yorumda adı geçen (henüz kredisiz)</option>
            </select>
            <select value={f.dispute} onChange={e => setFilter("dispute", e.target.value)} className={inputCls} data-testid="filter-dispute" title="İtiraz durumuna göre (Excel'e aktarmada da uygulanır)">
              <option value="">İtiraz: hepsi</option>
              <option value="hide_removed">Kaldırılanları gizle</option>
              <option value="hide_disputed">İtiraz edilenleri ve kaldırılanları gizle</option>
              <option value="open">İtirazı açık olanlar</option>
              <option value="removed">Sadece kaldırılanlar</option>
            </select>
            <select value={f.date_by} onChange={e => setFilter("date_by", e.target.value)} className={inputCls} data-testid="filter-date-by" title="Tarih aralığı hangi tarihe göre uygulansın">
              <option value="">Yorum tarihi</option>
              <option value="tour">Tur tarihi</option>
            </select>
            <input type="date" value={f.date_from} onChange={e => setFilter("date_from", e.target.value)} className={inputCls} data-testid="filter-date-from" />
            <span className="text-slate-500 text-xs">—</span>
            <input type="date" value={f.date_to} onChange={e => setFilter("date_to", e.target.value)} className={inputCls} data-testid="filter-date-to" />
            {hasFilters && <button onClick={() => { setF(EMPTY_F); setQ(""); }} className="flex items-center gap-1 text-xs text-slate-400 hover:text-white" data-testid="clear-filters"><X className="w-3 h-3" /> Temizle</button>}
            <span className="ml-auto text-xs text-slate-500" data-testid="reviews-count">{items.length} / {total}{capped ? "+" : ""} yorum</span>
            {(f.matched === "garson_mismatch" || mismatchScan?.running) && (
              <button onClick={runMismatchScan} disabled={mismatchScan?.running} className={`${btnCls} bg-amber-600 hover:bg-amber-500 text-white py-1.5 disabled:opacity-70`} data-testid="scan-mismatch-btn" title="Eşleşmiş yorumlarda metindeki garson ≠ atanan garson durumlarını tarar">
                <Sparkles className="w-4 h-4" />
                {mismatchScan?.running ? `Taranıyor… ${mismatchScan.done}/${mismatchScan.total} · ${mismatchScan.flagged}` : "Uyuşmazlıkları Tara"}
              </button>
            )}
            {(f.matched === "suggested" && ((stats?.totals?.suggested || 0) > 0 || acceptAll?.running)) && (
              <button onClick={runAcceptAll} disabled={acceptAll?.running} className={`${btnCls} bg-blue-600 hover:bg-blue-500 text-white py-1.5 disabled:opacity-70`} data-testid="accept-all-btn" title="Bekleyen tüm önerileri sırayla onaylar">
                <Sparkles className="w-4 h-4" />
                {acceptAll?.running ? `Onaylanıyor… ${acceptAll.done}/${acceptAll.total}` : `Tümünü Onayla (${stats?.totals?.suggested || 0})`}
              </button>
            )}
            {(f.matched === "garson_suggested" && ((stats?.totals?.garson_suggested || 0) > 0 || acceptCreditAll?.running)) && (
              <button onClick={runAcceptCreditAll} disabled={acceptCreditAll?.running} className={`${btnCls} bg-violet-700 hover:bg-violet-600 text-white py-1.5 disabled:opacity-70`} data-testid="accept-all-credit-btn" title="Bekleyen tüm garson önerilerini birincil adaya onaylar">
                <Sparkles className="w-4 h-4" />
                {acceptCreditAll?.running ? `Onaylanıyor… ${acceptCreditAll.done}/${acceptCreditAll.total}` : `Tümünü Onayla (${stats?.totals?.garson_suggested || 0})`}
              </button>
            )}
            <button onClick={exportList} disabled={exporting} className={`${btnCls} bg-emerald-700 hover:bg-emerald-600 text-white py-1.5`} data-testid="export-reviews-btn">
              <FileSpreadsheet className="w-4 h-4" /> {exporting ? "Hazırlanıyor…" : "Excel'e Aktar"}
            </button>
          </div>

          <div className={`space-y-2.5 transition-opacity ${loading && items.length ? "opacity-50 pointer-events-none" : ""}`} data-testid="reviews-list">
            {loading && items.length > 0 && <div className="text-xs text-blue-500 flex items-center gap-1.5" data-testid="reviews-loading"><RefreshCw className="w-3 h-3 animate-spin" /> Filtre uygulanıyor…</div>}
            {loading && items.length === 0 ? <div className="py-12 text-center text-slate-500 text-sm">Yükleniyor…</div>
              : items.length === 0 ? (
                <div className="py-12 text-center text-slate-500 text-sm border border-dashed border-slate-700 rounded-xl" data-testid="reviews-empty">
                  {hasFilters ? "Filtreyle eşleşen yorum yok" : "Henüz yorum yok. 'Yorum Ekle' veya 'Dosyadan Aktar' ile başlayın."}
                </div>
              ) : items.map(r => <ReviewRow key={r.id} r={r} onMatch={setMatchFor} onUnmatch={unmatch} onDelete={remove} onAssign={setAssignFor} onAcceptSuggestion={acceptSuggestion} onRejectSuggestion={rejectSuggestion} onCreditGarson={creditGarson} onUncreditGarson={uncreditGarson} onUnassignGarson={unassignGarson} onAcceptCredit={acceptCredit} onRejectCredit={rejectCredit} onDispute={setDisputeFor} />)}
          </div>

          {total > PAGE && (() => {
            const totalPages = Math.ceil(total / PAGE);
            const cur = page;
            const win = [];
            for (let i = 0; i < totalPages; i++) {
              if (i === 0 || i === totalPages - 1 || (i >= cur - 2 && i <= cur + 2)) win.push(i);
              else if (win[win.length - 1] !== "…") win.push("…");
            }
            const go = (p) => { load(p); window.scrollTo({ top: 0, behavior: "smooth" }); };
            return (
              <div className="flex items-center justify-center gap-1.5 mt-5 flex-wrap" data-testid="reviews-pagination">
                <button disabled={cur === 0 || loading} onClick={() => go(cur - 1)} className="px-2.5 py-1.5 rounded-lg text-sm bg-slate-700 hover:bg-slate-600 text-slate-200 disabled:opacity-40" data-testid="page-prev">‹ Önceki</button>
                {win.map((n, i) => n === "…"
                  ? <span key={`e${i}`} className="px-1.5 text-slate-500">…</span>
                  : <button key={n} onClick={() => go(n)} disabled={loading} data-testid={`page-${n + 1}`}
                      className={`min-w-[36px] px-2.5 py-1.5 rounded-lg text-sm font-semibold ${n === cur ? "bg-blue-600 text-white" : "bg-slate-700 hover:bg-slate-600 text-slate-200"}`}>{n + 1}</button>)}
                <button disabled={cur >= totalPages - 1 || loading} onClick={() => go(cur + 1)} className="px-2.5 py-1.5 rounded-lg text-sm bg-slate-700 hover:bg-slate-600 text-slate-200 disabled:opacity-40" data-testid="page-next">Sonraki ›</button>
                <span className="ml-2 text-xs text-slate-500">Sayfa {cur + 1}/{totalPages}{capped ? "+" : ""}</span>
              </div>
            );
          })()}
        </>
      )}

      <AssignModal review={assignFor} garsonlar={garsonlar} onClose={() => setAssignFor(null)} onDone={() => { setAssignFor(null); refreshAll(); }} />
      <MatchModal review={matchFor} onClose={() => setMatchFor(null)} onDone={() => { setMatchFor(null); refreshAll(); }} />
      <AddReviewModal open={showAdd} onClose={() => setShowAdd(false)} onDone={() => { setShowAdd(false); refreshAll(); }} />
      <Modal open={!!disputeFor} onClose={() => { setDisputeFor(null); load(page); }} title="İtiraz Takibi" width="max-w-2xl" testId="dispute-modal">
        {disputeFor && <div className="-m-5"><DisputePanel reviewId={disputeFor.id} /></div>}
      </Modal>
      <ImportModal open={showImport} onClose={() => setShowImport(false)} onDone={refreshAll} />
      </div>
    </div>
  );
}
